import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { SimpleDropdown } from './ui/simple-dropdown';
import { X, Save, User, Briefcase } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { SchoolManagerEditRequest } from '../api/types';
import { educationAPI } from '../api/axiosClient';

interface SchoolManagerEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  schoolManagerId: string | null;
  onSuccess: () => void;
}

const SchoolManagerEditForm: React.FC<SchoolManagerEditFormProps> = ({
  isOpen,
  onClose,
  schoolManagerId,
  onSuccess
}) => {
  const handleClose = () => {
    lastLoadedSchoolManagerId.current = null;
    onClose();
  };
  const [formData, setFormData] = useState<SchoolManagerEditRequest>({
    id: '',
    userId: '',
    firstName: '',
    lastName: '',
    title: '',
    isPrimary: false,
    twoFactorEnabled: false,
    userStatus: 1
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const lastLoadedSchoolManagerId = useRef<string | null>(null);


  // Country codes removed - no longer needed

  const userStatuses = useMemo(() => [
    { id: '1', label: 'Aktif', value: '1' },
    { id: '2', label: 'Pasif', value: '2' },
    { id: '3', label: 'Doğrulama Gerekli', value: '3' },
    { id: '4', label: 'Şifre Gerekli', value: '4' },
    { id: '5', label: 'Kilitli', value: '5' },
    { id: '6', label: 'Askıya Alındı', value: '6' },
    { id: '7', label: 'Devre Dışı', value: '7' },
    { id: '8', label: 'Şifre Değiştirmeli', value: '8' }
  ], []);


  // Load school manager data when schoolManagerId changes
  useEffect(() => {
    const loadSchoolManagerData = async () => {
      if (!schoolManagerId) {
        return;
      }
      
      // Aynı ID için zaten yükleme yapıldıysa tekrar yapma
      if (lastLoadedSchoolManagerId.current === schoolManagerId) {
        return;
      }

      lastLoadedSchoolManagerId.current = schoolManagerId;
      setIsLoading(true);
      try {
        const response = await educationAPI.getSchoolManagerDetails(schoolManagerId);
        
        if (response.success && response.data) {
          const data = response.data as any; // API response structure
          setFormData({
            id: data.id,
            userId: data.user.userId,
            firstName: data.user.firstName,
            lastName: data.user.lastName,
            title: data.title,
            isPrimary: data.isPrimary,
            userStatus: data.user.userStatus || 1,
            twoFactorEnabled: data.user.twoFactorEnabled || false
          });
        } else {
          toast.error('Okul müdürü bilgileri yüklenirken hata oluştu');
        }
      } catch (error) {
        console.error('Error loading school manager data:', error);
        toast.error('Okul müdürü bilgileri yüklenirken hata oluştu');
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && schoolManagerId) {
      loadSchoolManagerData();
    }
  }, [isOpen, schoolManagerId]);

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Ad alanı zorunludur';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Soyad alanı zorunludur';
    }


    if (!formData.title.trim()) {
      newErrors.title = 'Unvan alanı zorunludur';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const request = {
        id: formData.id,
        userId: formData.userId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        title: formData.title,
        isPrimary: formData.isPrimary,
        twoFactorEnabled: formData.twoFactorEnabled,
        userStatus: formData.userStatus
      };

      const response = await educationAPI.updateSchoolManager(formData.id, request);
      
      if (response.success) {
        toast.success('Okul müdürü başarıyla güncellendi');
        onSuccess();
        handleClose();
      } else {
        toast.error(response.message || 'Okul müdürü güncellenirken hata oluştu');
      }
    } catch (error) {
      console.error('Error updating school manager:', error);
      toast.error('Okul müdürü güncellenirken hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof SchoolManagerEditRequest, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="text-xl font-semibold">Okul Yöneticisi Düzenle</CardTitle>
            <CardDescription>
              Okul yöneticisi bilgilerini güncelleyin
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Ad */}
                <div>
                  <Label htmlFor="firstName" className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Ad <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={errors.firstName ? 'border-red-500' : ''}
                    placeholder="Adını girin"
                  />
                  {errors.firstName && (
                    <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>
                  )}
                </div>

                {/* Soyad */}
                <div>
                  <Label htmlFor="lastName" className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Soyad <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={errors.lastName ? 'border-red-500' : ''}
                    placeholder="Soyadını girin"
                  />
                  {errors.lastName && (
                    <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>
                  )}
                </div>


                {/* Unvan */}
                <div>
                  <Label htmlFor="title" className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Briefcase className="w-4 h-4 mr-2" />
                    Unvan <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Input
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className={errors.title ? 'border-red-500' : ''}
                    placeholder="Müdür, Müdür Yardımcısı, vb."
                  />
                  {errors.title && (
                    <p className="text-sm text-red-600 mt-1">{errors.title}</p>
                  )}
                </div>

                {/* Kullanıcı Durumu */}
                <div>
                  <Label htmlFor="userStatus" className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Kullanıcı Durumu <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <SimpleDropdown
                    options={userStatuses}
                    value={userStatuses.find(status => status.value === formData.userStatus?.toString()) || userStatuses[0]}
                    onChange={(status) => {
                      if (status) {
                        setFormData(prev => ({ ...prev, userStatus: parseInt(status.value as string) }));
                      }
                    }}
                    placeholder="Durum seçin"
                    className="w-full"
                  />
                </div>


                {/* İki Faktörlü Kimlik Doğrulama */}
                <div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="twoFactorEnabled"
                      checked={formData.twoFactorEnabled || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, twoFactorEnabled: e.target.checked }))}
                      className="w-4 h-4 text-[#9B51E0] bg-gray-100 border-gray-300 rounded focus:ring-[#9B51E0] focus:ring-2"
                    />
                    <label htmlFor="twoFactorEnabled" className="text-sm font-medium text-gray-700">
                      İki Faktörlü Kimlik Doğrulama
                    </label>
                  </div>
                </div>


              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-8 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#9B51E0] hover:bg-[#8B4AD9] text-white"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Kaydediliyor...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Save className="w-4 h-4 mr-2" />
                      Kaydet
                    </div>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SchoolManagerEditForm;
